import { readStoredTokenRaw } from './auth-storage';

/**
 * Čtení přihlašovacího tokenu (JWT) v prohlížeči — bez knihovny a **bez ověření podpisu**.
 *
 * Slouží jen k rozhodnutí v UI, jestli má smysl pouštět uživatele dál (EL-2290).
 * O platnosti tokenu rozhoduje vždycky server; podvržený token s dalekým `exp`
 * tu projde, ale na API dostane 401.
 */

/**
 * Token uložený po přihlášení, nebo `undefined`, když žádný není nebo je nečitelný.
 * V záložce náhledu kurzu je to token náhledového studenta (`auth-storage.ts`, EL-2357).
 */
export function storedToken(): string | undefined {
  const raw = readStoredTokenRaw();

  if (raw === null) {
    return undefined;
  }

  try {
    const token = JSON.parse(raw);

    return typeof token === 'string' && token.length > 0 ? token : undefined;
  } catch {
    return undefined;
  }
}

/** Obsah tokenu (payload), nebo `undefined`, když to není čitelný JWT. */
export function jwtPayload(token: string): { [claim: string]: unknown } | undefined {
  const parts = token.split('.');

  if (parts.length !== 3 || !parts[1]) {
    return undefined;
  }

  try {
    const payload = JSON.parse(decodeBase64Url(parts[1]));

    return payload !== null && typeof payload === 'object' ? payload : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Id přihlášeného uživatele z tokenu (`nameid`, případně `sub`), nebo `undefined`.
 * Jen pro rozhodnutí v prohlížeči — o identitě rozhoduje server.
 */
export function jwtUserId(token: string): string | undefined {
  const payload = jwtPayload(token);
  const id = payload?.['nameid'] ?? payload?.['sub'];

  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

/**
 * Id vydavatele, který náhled kurzu otevřel (claim `previewBy`, EL-2357), nebo `undefined`.
 * Token běžného přihlášení ho nemá. Jen pro UI (pruh náhledu) — co token smí, hlídá server.
 */
export function jwtPreviewBy(token: string): string | undefined {
  const previewBy = jwtPayload(token)?.['previewBy'];

  return typeof previewBy === 'string' && previewBy.length > 0 ? previewBy : undefined;
}

/** Okamžik vypršení tokenu v milisekundách, nebo `undefined`, když ho token neuvádí. */
export function jwtExpiresAt(token: string): number | undefined {
  const exp = jwtPayload(token)?.['exp'];

  return typeof exp === 'number' ? exp * 1000 : undefined;
}

/**
 * Vypršel token?
 *
 * Nečitelný token se bere jako vypršelý — server by ho stejně odmítl a uživatel by
 * zůstal na prázdné stránce. Token bez `exp` naopak vypršelý není: rozhodne server.
 */
export function isJwtExpired(token: string, now: number = Date.now()): boolean {
  if (!jwtPayload(token)) {
    return true;
  }

  const expiresAt = jwtExpiresAt(token);

  return expiresAt !== undefined && expiresAt <= now;
}

/**
 * base64url → text. Payload je UTF-8 (jméno i e-mail můžou mít diakritiku), `atob`
 * vrací bajty jako znaky, proto ten mezikrok přes `TextDecoder`.
 */
function decodeBase64Url(value: string): string {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));

  return new TextDecoder().decode(bytes);
}
